import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ShoppingCart, 
  Search, 
  Filter,
  Eye,
  RefreshCw,
  Calendar,
  DollarSign,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Trash2
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const Orders = () => {
  const { isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [resellerEmailFilter, setResellerEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen && !event.target.closest('.status-dropdown-container')) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const { data: ordersData, isLoading, refetch } = useQuery(
    ['orders', { page, limit, status: statusFilter }],
    () => ordersAPI.getAll({ 
      page, 
      limit, 
      ...(statusFilter && { status: statusFilter })
    }),
    {
      select: (response) => response.data.data,
      keepPreviousData: true
    }
  );

  const orders = ordersData?.orders || [];
  const pagination = ordersData?.pagination || {};

  // Filter orders based on search term and reseller email (admin only)
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.variant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.customer_email && order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesResellerEmail = !resellerEmailFilter || 
      (order.customer_email && order.customer_email.toLowerCase().includes(resellerEmailFilter.toLowerCase()));
    
    return matchesSearch && matchesResellerEmail;
  });

  const handleRetryOrder = async (orderId) => {
    if (!isAdmin) return;
    
    try {
      await ordersAPI.retry(orderId);
      toast.success('Order retry initiated');
      refetch();
    } catch (error) {
      toast.error('Failed to retry order');
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber, e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await ordersAPI.delete(orderId);
      toast.success('Order deleted successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'badge-success',
      pending: 'badge-warning',
      failed: 'badge-danger',
      processing: 'badge-info'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading && page === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Filters */}
      <div className="orders-search-container">
      <div className="card orders-search-box" style={{ overflow: 'visible' }}>
        <div className="card-content" style={{ overflow: 'visible' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-gray-100 placeholder-gray-400"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    outline: 'none',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px #3b198e';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = '';
                  }}
                />
              </div>
            </div>

            {/* Reseller Email Filter - Admin Only */}
            {isAdmin && (
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter by reseller email..."
                    value={resellerEmailFilter}
                    onChange={(e) => setResellerEmailFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg text-gray-100 placeholder-gray-400"
                    style={{ 
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid #4a4a4a',
                      outline: 'none',
                      transition: 'box-shadow 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px #3b198e';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = '';
                    }}
                  />
                </div>
              </div>
            )}

            {/* Status Filter */}
            <div className="sm:w-48 status-dropdown-container" style={{ position: 'relative' }}>
              <div
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                style={{ 
                  width: '100%',
                  backgroundColor: '#0a0a0a', 
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  paddingLeft: '1rem',
                  paddingRight: '2.5rem',
                  cursor: 'pointer',
                  position: 'relative',
                  color: '#ffffff',
                  transition: 'border-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
              >
                {statusFilter === '' ? 'All Statuses' : 
                 statusFilter === 'pending' ? 'Pending' :
                 statusFilter === 'completed' ? 'Completed' :
                 statusFilter === 'failed' ? 'Failed' : 'Processing'}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#ffffff" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: statusDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                    width: '1.25rem',
                    height: '1.25rem',
                    transition: 'transform 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              
              {statusDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    animation: 'dropdownSlide 0.2s ease-out'
                  }}
                >
                  {['', 'pending', 'completed', 'failed', 'processing'].map((status) => (
                    <div
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setStatusDropdownOpen(false);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: '#ffffff',
                        fontSize: '0.875rem',
                        transition: 'background-color 0.15s ease',
                        backgroundColor: statusFilter === status ? '#3a3a3a' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = statusFilter === status ? '#3a3a3a' : 'transparent'}
                    >
                      {status === '' ? 'All Statuses' : 
                       status === 'pending' ? 'Pending' :
                       status === 'completed' ? 'Completed' :
                       status === 'failed' ? 'Failed' : 'Processing'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-white">No orders found</h3>
          <p className="mt-1 text-sm text-white">
            {searchTerm || statusFilter 
              ? 'Try adjusting your search or filter criteria.'
              : 'Orders will appear here once they are created.'
            }
          </p>
        </div>
      ) : (
        <div className="orders-list-container">
        <div className="card orders-list-box">
          <div className="overflow-x-auto">
            <table className="table" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Product</th>
                  <th>Variant</th>
                  <th>Quantity</th>
                  <th>Amount</th>
                  {isAdmin && <th>Reseller</th>}
                  <th>Source</th>
                  <th>Status</th>
                  <th>Date</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="transition-colors" style={{ cursor: 'pointer', border: 'none' }} onClick={() => window.location.href = `/orders/${order.order_number}`} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td style={{ border: 'none' }}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-2">
                          {getStatusIcon(order.status)}
                        </div>
                        <div 
                          className="font-medium"
                          style={{ 
                            color: '#3b198e',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          {order.order_number}
                        </div>
                      </div>
                    </td>
                    <td style={{ border: 'none' }}>
                      <div className="flex items-center">
                        <Package className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-white">{order.product_name}</span>
                      </div>
                    </td>
                    <td style={{ border: 'none' }}>
                      <span className="text-white">{order.variant_name}</span>
                    </td>
                    <td style={{ border: 'none' }}>
                      <span className="text-white font-medium">{order.quantity}</span>
                    </td>
                    <td style={{ border: 'none' }}>
                      {order.total_amount ? (
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                          <span className="text-white font-medium">
                            {parseFloat(order.total_amount).toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ border: 'none' }}>
                        <span className="text-white">
                          {order.customer_email || '-'}
                        </span>
                      </td>
                    )}
                    <td style={{ border: 'none' }}>
                      <span className="badge badge-secondary capitalize">
                        {order.payment_method || order.order_source || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ border: 'none' }}>
                      {getStatusBadge(order.status)}
                    </td>
                    <td style={{ border: 'none' }}>
                      <div className="flex items-center text-sm text-white">
                        <Calendar className="h-4 w-4 mr-1" />
                        {format(new Date(order.created_at + 'Z'), 'MMM dd, yyyy h:mm a')}
                      </div>
                    </td>
                    {isAdmin && (
                      <td style={{ border: 'none' }}>
                        <button
                          onClick={(e) => handleDeleteOrder(order.id, order.order_number, e)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Delete order"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
            <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ 
                    opacity: page === 1 ? 0.5 : 1,
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    minWidth: '40px',
                    minHeight: '40px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {/* Page Number Tabs */}
                <div className="flex gap-1">
                  {Array.from({ length: pagination.pages || 1 }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      style={{
                        minWidth: '40px',
                        minHeight: '40px',
                        backgroundColor: pageNum === page ? '#2d1470' : 'transparent',
                        color: pageNum === page ? '#ffffff' : '#9ca3af',
                        border: 'none',
                        fontWeight: pageNum === page ? '600' : '400',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.color = '#ffffff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (pageNum !== page) {
                          e.currentTarget.style.color = '#9ca3af';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(pagination.pages || 1, p + 1))}
                  disabled={page === (pagination.pages || 1)}
                  style={{ 
                    opacity: page === (pagination.pages || 1) ? 0.5 : 1,
                    cursor: page === (pagination.pages || 1) ? 'not-allowed' : 'pointer',
                    minWidth: '40px',
                    minHeight: '40px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            
            {/* Showing X to X of X Orders - Bottom Right */}
            <div className="text-sm text-white">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total || 0)} of {pagination.total || 0} orders
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading overlay for pagination */}
      {isLoading && page > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
